import { Transform, Type } from 'class-transformer';
import {
	IsArray,
	IsBoolean,
	IsIn,
	IsInt,
	IsISO8601,
	IsOptional,
	IsString,
	IsUUID,
	Length,
	Matches,
	Max,
	MaxLength,
	Min,
	registerDecorator,
	type ValidationOptions,
} from 'class-validator';

const trimString = ({ value }: { value: unknown }) =>
	typeof value === 'string' ? value.trim() : value;

export function isValidTimeZone(value: unknown): boolean {
	if (typeof value !== 'string' || !value) {
		return false;
	}
	try {
		new Intl.DateTimeFormat('en-US', { timeZone: value });

		return true;
	} catch {
		return false;
	}
}

export function IsTimeZone(validationOptions?: ValidationOptions) {
	return (object: object, propertyName: string) => {
		registerDecorator({
			name: 'isTimeZone',
			target: object.constructor,
			propertyName,
			options: {
				message: `${propertyName} must be a valid IANA time zone`,
				...validationOptions,
			},
			validator: { validate: isValidTimeZone },
		});
	};
}

const ISO_DATE_TIME_OPTIONS = { strict: true, strictSeparator: true } as const;

export const COURSES_DATE_ONLY_REGEX = /^\d{4}-\d{2}-\d{2}$/;
export const COURSES_AVAILABILITY_TIME_REGEX = /^\d{2}:\d{2}$/;

export const COURSES_AVAILABILITY_KINDS = ['recurring', 'one_off'] as const;
export type CoursesAvailabilityKind = (typeof COURSES_AVAILABILITY_KINDS)[number];

export const COURSES_BLACKOUT_RECURRENCES = ['none', 'weekly'] as const;
export type CoursesBlackoutRecurrence = (typeof COURSES_BLACKOUT_RECURRENCES)[number];

export const COURSES_OFFICE_HOUR_BOOKING_STATUSES = [
	'confirmed',
	'cancelled',
	'completed',
] as const;
export type CoursesOfficeHourBookingStatus = (typeof COURSES_OFFICE_HOUR_BOOKING_STATUSES)[number];

export const COURSES_BOOKING_CONFLICT_CODE = 'BOOKING_CONFLICT';
export const COURSES_BOOKING_NOT_ACTIVE_CODE = 'BOOKING_NOT_ACTIVE';

export class CoursesOfficeHoursCourseQueryDto {
	@IsUUID()
	courseId: string;
}

export class CoursesMentorAvailabilityQueryDto {
	@IsUUID()
	mentorId: string;

	@IsISO8601(ISO_DATE_TIME_OPTIONS)
	from: string;

	@IsISO8601(ISO_DATE_TIME_OPTIONS)
	to: string;

	@IsOptional()
	@IsUUID()
	courseId?: string | null;
}

export class CoursesCourseAvailabilityGridQueryDto {
	@IsUUID()
	courseId: string;

	@IsISO8601(ISO_DATE_TIME_OPTIONS)
	from: string;

	@IsISO8601(ISO_DATE_TIME_OPTIONS)
	to: string;
}

export class CoursesSuggestNextSlotsQueryDto {
	@IsUUID()
	mentorId: string;

	@IsOptional()
	@IsUUID()
	courseId?: string | null;

	@IsISO8601(ISO_DATE_TIME_OPTIONS)
	afterIso: string;

	@IsOptional()
	@Type(() => Number)
	@IsInt()
	@Min(1)
	@Max(10)
	limit?: number;

	@IsOptional()
	@Type(() => Number)
	@IsInt()
	@Min(1)
	@Max(60)
	horizonDays?: number;
}

export class CoursesCreateBookingDto {
	@IsUUID()
	courseId: string;

	@IsUUID()
	mentorId: string;

	@IsISO8601(ISO_DATE_TIME_OPTIONS)
	startsAt: string;

	@IsOptional()
	@Transform(trimString)
	@IsString()
	@MaxLength(1000)
	studentNote?: string | null;
}

export class CoursesListBookingsQueryDto {
	@IsOptional()
	@Transform(({ value }) => value === true || value === 'true')
	@IsBoolean()
	upcoming?: boolean;

	@IsOptional()
	@IsIn(COURSES_OFFICE_HOUR_BOOKING_STATUSES)
	status?: CoursesOfficeHourBookingStatus;

	@IsOptional()
	@Type(() => Number)
	@IsInt()
	@Min(1)
	@Max(500)
	limit?: number;

	@IsOptional()
	@Type(() => Number)
	@IsInt()
	@Min(0)
	offset?: number;
}

export class CoursesRescheduleBookingDto {
	@IsISO8601(ISO_DATE_TIME_OPTIONS)
	startsAt: string;
}

export class CoursesUpsertAvailabilityDto {
	@IsOptional()
	@IsUUID()
	id?: string;

	@IsIn(COURSES_AVAILABILITY_KINDS)
	kind: CoursesAvailabilityKind;

	@IsOptional()
	@IsInt()
	@Min(0)
	@Max(6)
	weekday?: number | null;

	@IsOptional()
	@IsString()
	@Matches(COURSES_DATE_ONLY_REGEX)
	date?: string | null;

	@IsString()
	@Matches(COURSES_AVAILABILITY_TIME_REGEX)
	start_time: string;

	@IsString()
	@Matches(COURSES_AVAILABILITY_TIME_REGEX)
	end_time: string;

	@IsString()
	@Length(1, 64)
	@IsTimeZone()
	timezone: string;

	@IsOptional()
	@IsBoolean()
	active?: boolean;
}

export class CoursesUpdateOfficeSettingsDto {
	@IsInt()
	@Min(10)
	@Max(240)
	slot_minutes: number;

	@IsInt()
	@Min(0)
	@Max(180)
	buffer_minutes: number;

	@IsInt()
	@Min(0)
	@Max(720)
	min_notice_hours: number;

	@IsInt()
	@Min(1)
	@Max(365)
	max_advance_days: number;

	@IsString()
	@Length(1, 64)
	@IsTimeZone()
	timezone: string;
}

export class CoursesPreviewBlackoutConflictsDto {
	@IsISO8601(ISO_DATE_TIME_OPTIONS)
	startsAt: string;

	@IsISO8601(ISO_DATE_TIME_OPTIONS)
	endsAt: string;

	@IsOptional()
	@IsUUID()
	courseId?: string | null;

	/** Defaults to `none` in the service. */
	@IsOptional()
	@IsIn(COURSES_BLACKOUT_RECURRENCES)
	recurrence?: CoursesBlackoutRecurrence;

	@IsOptional()
	@IsString()
	@Matches(COURSES_DATE_ONLY_REGEX)
	recurrenceUntil?: string | null;

	/** Defaults to `[]` in the service. */
	@IsOptional()
	@IsArray()
	@IsString({ each: true })
	@Matches(COURSES_DATE_ONLY_REGEX, { each: true })
	exceptions?: string[];
}

export class CoursesAddBlackoutDto extends CoursesPreviewBlackoutConflictsDto {
	@IsOptional()
	@Transform(trimString)
	@IsString()
	@MaxLength(500)
	reason?: string | null;
}

export class CoursesUpdateBlackoutExceptionsDto {
	@IsArray()
	@IsString({ each: true })
	@Matches(COURSES_DATE_ONLY_REGEX, { each: true })
	exceptions: string[];
}

export class CoursesAdminAssignMentorDto {
	@IsUUID()
	courseId: string;

	@IsUUID()
	userId: string;

	@IsOptional()
	@Transform(trimString)
	@IsString()
	@MaxLength(100)
	roleLabel?: string | null;
}

export class CoursesAdminSetOfficeHoursEnabledDto {
	@IsBoolean()
	enabled: boolean;
}

export class CoursesAdminBlackoutAuditQueryDto {
	/** Defaults to 100 in the service. */
	@IsOptional()
	@Type(() => Number)
	@IsInt()
	@Min(1)
	@Max(200)
	limit?: number;
}

/** Query Google appends to the office-hours Calendar OAuth redirect. */
export class CoursesGoogleCalendarCallbackQueryDto {
	@IsOptional()
	@IsString()
	@MaxLength(2048)
	code?: string;

	@IsOptional()
	@IsString()
	@MaxLength(2048)
	state?: string;

	@IsOptional()
	@IsString()
	@MaxLength(200)
	error?: string;
}
